import { useEffect, useRef, useState } from "react";
import type { ViewedFile } from "../../api/admin";
import { messageOf } from "../../lib/errors";

interface FileViewerProps {
  title: string;
  subtitle?: string | undefined;
  load: () => Promise<ViewedFile>;
  onClose: () => void;
}

/*
 * View-only viewer for participant CVs and KYC screenshots.
 *
 * Admins never get a file: no download link, no browser PDF viewer (its toolbar has a Download button), no
 * `<a href>` to an object. PDFs are drawn page by page onto canvases with pdf.js; images sit in an <img> from an
 * in-memory blob; Word CVs arrive as server-made HTML and are shown with every script/link/style stripped.
 * Right-click, drag, save and print shortcuts are blocked inside the viewer. That deters casual saving; it can't
 * stop a screenshot, and isn't meant to.
 */
export function FileViewer({ title, subtitle, load, onClose }: FileViewerProps) {
  const [file, setFile] = useState<ViewedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const dialog = useRef<HTMLDivElement>(null);

  /*
   * One request per opened file. React StrictMode (development) mounts, unmounts and remounts every component, which
   * ran this effect twice: the file was fetched twice and the activity log got two "Document viewed" entries. The ref
   * survives that remount, so the second run reuses the first run's request.
   */
  const request = useRef<{ load: typeof load; promise: Promise<ViewedFile> } | null>(null);

  useEffect(() => {
    let live = true;
    if (request.current?.load !== load) request.current = { load, promise: load() };
    request.current.promise
      .then((loaded) => live && setFile(loaded))
      .catch((e: unknown) => live && setError(messageOf(e)));
    return () => {
      live = false;
    };
  }, [load]);

  // Release image blobs when the viewer closes.
  useEffect(() => () => void (file?.kind === "image" && URL.revokeObjectURL(file.url)), [file]);

  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if ((event.metaKey || event.ctrlKey) && ["s", "p"].includes(event.key.toLowerCase()))
        event.preventDefault();
      if (event.key === "+" || event.key === "=") setZoom((z) => Math.min(3, z + 0.25));
      if (event.key === "-") setZoom((z) => Math.max(0.5, z - 0.25));
    };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  const block = (event: React.SyntheticEvent) => event.preventDefault();

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-[#1d1214]/90 print:hidden"
      onClick={onClose}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={`${title}, view only`}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onContextMenu={block}
        onDragStart={block}
        onCopy={block}
        className="flex min-h-0 flex-1 flex-col outline-none"
      >
        <header className="flex items-center gap-3 border-b border-white/10 bg-[#2a1a1d] px-4 py-3 text-white">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-bold">{title}</div>
            <div className="truncate text-[12px] text-white/60">
              {subtitle ? `${subtitle} · ` : ""}View only. Downloading is disabled.
            </div>
          </div>
          {file && file.kind !== "unsupported" ? (
            <div
              className="flex items-center gap-1 rounded-[9px] bg-white/10 p-1"
              aria-label="Zoom"
            >
              <button
                type="button"
                aria-label="Zoom out"
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                className="h-8 w-8 rounded-[7px] text-[18px] hover:bg-white/15"
              >
                −
              </button>
              <span className="w-12 text-center text-[12px] tabular">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                aria-label="Zoom in"
                onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                className="h-8 w-8 rounded-[7px] text-[18px] hover:bg-white/15"
              >
                +
              </button>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-[9px] bg-white/10 px-3 py-2 text-[13px] font-bold hover:bg-white/20"
          >
            Close
          </button>
        </header>

        <div
          className="min-h-0 flex-1 select-none overflow-auto p-4 sm:p-8"
          style={{ WebkitUserSelect: "none" }}
        >
          {error ? (
            <div
              role="alert"
              className="mx-auto mt-16 max-w-[420px] rounded-[var(--radius-card)] bg-surface p-6 text-center"
            >
              <b className="block text-[16px] text-accent">We couldn't open this file.</b>
              <p className="m-0 mt-1 text-[14px] text-text-muted">{error}</p>
            </div>
          ) : !file ? (
            <div
              role="status"
              aria-label="Loading file"
              className="mx-auto mt-16 h-[60vh] max-w-[680px] animate-pulse rounded-[6px] bg-white/10"
            />
          ) : file.kind === "pdf" ? (
            <PdfPages data={file.data} zoom={zoom} />
          ) : file.kind === "image" ? (
            <img
              src={file.url}
              alt={title}
              draggable={false}
              className="mx-auto block max-w-none rounded-[6px] bg-white shadow-2xl"
              style={{ width: `${zoom * 100}%`, maxWidth: `${zoom * 900}px` }}
            />
          ) : file.kind === "html" ? (
            <article
              className="cv-html mx-auto rounded-[6px] bg-white p-8 text-[14px] leading-[1.6] text-[#231416] shadow-2xl sm:p-12"
              style={{ maxWidth: `${720 * zoom}px`, fontSize: `${14 * zoom}px` }}
              // Server-converted Word HTML, re-sanitised here: text, headings, lists and tables only.
              dangerouslySetInnerHTML={{ __html: sanitiseCvHtml(file.html) }}
            />
          ) : (
            <div className="mx-auto mt-16 max-w-[460px] rounded-[var(--radius-card)] bg-surface p-6 text-center">
              <b className="block text-[16px] text-accent">This CV is an old Word (.doc) file.</b>
              <p className="m-0 mt-1 text-[14px] text-text-muted">
                It can't be shown in the browser. Ask the participant to upload it again as a PDF or
                .docx.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Renders every page of a PDF onto its own canvas. */
function PdfPages({ data, zoom }: { data: ArrayBuffer; zoom: number }) {
  const holder = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const target = holder.current;
    if (!target) return;
    (async () => {
      const pdfjs = await import("pdfjs-dist");
      const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      // A copy: pdf.js transfers the buffer to its worker, and zooming re-renders from the same bytes.
      const doc = await pdfjs.getDocument({ data: data.slice(0), isEvalSupported: false }).promise;
      if (cancelled) return;
      setPages(doc.numPages);
      target.replaceChildren();
      const ratio = window.devicePixelRatio || 1;
      for (let n = 1; n <= doc.numPages; n++) {
        const page = await doc.getPage(n);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1.4 * zoom });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.setAttribute("aria-label", `Page ${n} of ${doc.numPages}`);
        canvas.setAttribute("role", "img");
        canvas.className = "mx-auto mb-4 block bg-white shadow-2xl";
        target.appendChild(canvas);
        const context = canvas.getContext("2d");
        if (!context) continue;
        context.scale(ratio, ratio);
        await page.render({ canvasContext: context, viewport }).promise;
      }
    })().catch((e: unknown) => !cancelled && setError(messageOf(e)));
    return () => {
      cancelled = true;
    };
  }, [data, zoom]);

  return (
    <>
      {pages ? (
        <p className="mb-3 text-center text-[12px] text-white/60">
          {pages} page{pages === 1 ? "" : "s"}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-center text-white">
          {error}
        </p>
      ) : null}
      <div ref={holder} />
    </>
  );
}

const ALLOWED = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "UL",
  "OL",
  "LI",
  "STRONG",
  "EM",
  "B",
  "I",
  "U",
  "BR",
  "TABLE",
  "THEAD",
  "TBODY",
  "TR",
  "TD",
  "TH",
  "SPAN",
]);

/** Keeps only plain document structure from the converted Word HTML: no scripts, links, images, styles or handlers. */
export function sanitiseCvHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      if (!ALLOWED.has(child.tagName)) {
        child.replaceWith(...Array.from(child.childNodes));
        walk(node);
        return;
      }
      for (const attr of Array.from(child.attributes)) child.removeAttribute(attr.name);
      walk(child);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}
